import {
	ExclamationCircleFilled,
	LoadingOutlined,
	PlusOutlined,
} from "@ant-design/icons";
import { formatTotalTime } from "../../common/utils/formatTotalTime.ts";
import { getUserDisplayName } from "../../common/utils/userDisplayName.ts";
import type { IntlShape } from "@formatjs/intl";
import type { Dispatch } from "@reduxjs/toolkit";
import {
	Button,
	Flex,
	Form,
	Select,
	Spin,
	Tag,
	Typography,
	type FormInstance,
} from "antd";
import type { AnyObject } from "antd/es/_util/type";
import { connect, type DispatchProp } from "react-redux";
import { actions as routerActions } from "redux-router5";
import type { State as RouterState } from "router5";
import ConnectedAppLayout from "../AppLayout.tsx";
import type { RootState } from "../store.ts";
import * as commonColumns from "../../common/columns.tsx";
import { ActiveCheckInBadge } from "../../common/components/ActiveCheckInBadge.tsx";
import { Card } from "../../common/components/Card.tsx";
import { DrawerItemWrapper } from "../../common/components/DrawerItemWrapper.tsx";
import { EditableDatePicker } from "../../common/components/EditableDatePicker.tsx";
import { ListActionBar } from "../../common/components/ListActionBar.tsx";
import { NoData } from "../../common/components/NoDataComp.tsx";
import { ReportDoneBadge } from "../../common/components/ReportDoneBadge.tsx";
import { CompletionProgress } from "../../common/components/CompletionProgress.tsx";
import { PageTitleContainer } from "../../common/components/PageTitle.tsx";
import {
	TableWrapper,
	type PaginationAndFilter,
	type TableWrapperProps,
} from "../../common/components/Table.tsx";
import { ticketFilter } from "../common/filter.ts";
import { sanitizedFilter } from "../../common/functions.ts";
import { routeNames } from "../common/routes.ts";
import {
	ticketActions,
	type SideloadedTicketInfo,
} from "../../features/ticket/ticketSlice.ts";
import type {
	Client,
	CurrentUser,
	PaginatedticketResultsSideloadList,
	Project,
	Services,
	Status,
	Ticket,
	Turbine,
	UserLimitedList,
} from "../generated-axios-client/index.ts";
import { AssigneeSelectorFormItem } from "./filter-forms/AssigneeSelectorFormItem.tsx";
import { ClientSelectorFormItem } from "./filter-forms/ClientSelectorFormItem.tsx";
import { CreatedBySelectorFormItem } from "./filter-forms/CreatedBySelectorFormItem.tsx";
import { EditableAssignees } from "./EditableAssignees.tsx";
import { StatusSelectorFormItem } from "./filter-forms/StatusSelectorFormItem.tsx";
import { TurbineSelectorFormItem } from "./filter-forms/TurbineSelectorFormItem.tsx";
import { statusColorMap, statusLabelMap } from "./ticketConstants.ts";

const onFilter =
	(dispatch: Dispatch, form: FormInstance, currentRoute: RouterState) => () => {
		form
			.validateFields()
			.then((values) => {
				dispatch(
					routerActions.navigateTo(
						currentRoute.name,
						sanitizedFilter({ ...currentRoute.params, ...values, page: 1 }),
					),
				);
			})
			.catch((error) => {
				console.log("Form validation error:", error);
			});
	};

const onCancel =
	(dispatch: Dispatch, form: FormInstance, currentRoute: RouterState) => () => {
		dispatch(
			routerActions.navigateTo(
				currentRoute.name,
				sanitizedFilter({
					...currentRoute.params,
					...ticketFilter,
					page: 1,
				}),
			),
		);
		form.resetFields();
	};

type rowDataType = {
	user?: CurrentUser;
	results: PaginatedticketResultsSideloadList;
	ticketSideload: SideloadedTicketInfo;
	serviceObjects: Record<string, Services> | null;
	technicians: UserLimitedList[];
};

const getColumns: TableWrapperProps<
	PaginatedticketResultsSideloadList,
	AnyObject
>["getColumns"] = (intl, currentFilter) => [
	commonColumns.simpleColumn(
		"ticket_number",
		"table_column_ticket_number",
		intl,
		currentFilter,
	),
	commonColumns.simpleColumn(
		"service",
		"table_column_service",
		intl,
		currentFilter,
		"service__name",
	),
	{
		dataIndex: "report",
		key: "report",
		title: intl.formatMessage({ id: "table_column_report" }),
		width: 130,
	},
	{
		dataIndex: "assignee",
		key: "assignee",
		title: intl.formatMessage({ id: "table_column_assignee" }, { count: 1 }),
		width: 160,
	},
	commonColumns.simpleColumn(
		"turbine",
		"table_column_turbine",
		intl,
		currentFilter,
		"turbine__serial_number",
	),
	commonColumns.simpleColumn(
		"location",
		"table_column_location",
		intl,
		currentFilter,
		"location__name",
	),
	{
		dataIndex: "progress",
		key: "progress",
		title: intl.formatMessage({ id: "table_column_progress" }),
		width: 150,
	},
	commonColumns.simpleColumn(
		"start",
		"table_column_start",
		intl,
		currentFilter,
	),
	commonColumns.simpleColumn(
		"deadline",
		"table_column_deadline",
		intl,
		currentFilter,
	),
	{
		dataIndex: "active_checkin",
		key: "active_checkin",
		title: intl.formatMessage({ id: "table_column_active_checkin" }),
		width: 130,
	},
	{
		dataIndex: "total_time",
		key: "total_time",
		title: intl.formatMessage({ id: "table_column_total_time" }),
		width: 110,
	},
	commonColumns.simpleColumn(
		"status",
		"table_column_status",
		intl,
		currentFilter,
	),
	commonColumns.simpleColumn("actions", "table_column_actions", intl),
];

const getTableRows: TableWrapperProps<rowDataType, AnyObject>["getRows"] = (
	intl,
	dispatch,
	data,
	modalApi,
) => {
	const { results, user, ticketSideload, serviceObjects, technicians } = data;

	return Object.values(results.results.tickets).map((ticket: Ticket) => {
		const showDeleteConfirm = () => {
			modalApi?.confirm({
				title: intl.formatMessage({ id: "ticket_delete_confirmation" }),
				icon: <ExclamationCircleFilled />,
				okText: intl.formatMessage({ id: "confirm" }),
				okType: "danger",
				cancelText: intl.formatMessage({ id: "cancel" }),
				onOk() {
					if (ticket.id) {
						dispatch(ticketActions.deleteTicket(ticket.id));
					}
				},
				onCancel() {},
			});
		};

		const onView = () => {
			dispatch(
				routerActions.navigateTo(routeNames.TICKET_VIEW, {
					id: ticket.id,
				}),
			);
		};

		const onViewTurbine = () => {
			dispatch(
				routerActions.navigateTo(routeNames.TURBINE_VIEW, {
					id: ticket.turbine,
				}),
			);
		};

		const onViewSite = () => {
			dispatch(
				routerActions.navigateTo(routeNames.LOCATION_VIEW, {
					id: ticket.location,
				}),
			);
		};

		const turbineLabel = ticketSideload.turbine[ticket.turbine]?.serial_number;
		const siteLabel = ticketSideload.location[ticket.location]?.name;

		const onEdit = () => {
			dispatch(
				routerActions.navigateTo(routeNames.TICKET_EDIT, { id: ticket.id }),
			);
		};
		const onDelete = () => {
			showDeleteConfirm();
		};

		const status =
			ticket.status && ticket.status in statusLabelMap ? (
				<Tag
					key={ticket.id}
					color={
						ticket.status in statusColorMap
							? statusColorMap[ticket.status]
							: "gray"
					}
					variant={"outlined"}
					styles={{ root: { fontWeight: 700 } }}
				>
					{statusLabelMap[ticket.status]}
				</Tag>
			) : (
				<NoData intl={intl} key={ticket.id} />
			);

		return {
			key: ticket.id,
			service: ticket.service ? serviceObjects?.[ticket.service]?.name : undefined,
			ticket_number: ticket.id ? (
				<Typography.Link
					strong
					onClick={onView}
				>{`#${ticket.ticket_number}`}</Typography.Link>
			) : (
				<Typography.Text
					strong
					type="secondary"
				>{`#${ticket.ticket_number}`}</Typography.Text>
			),
			client_case_nr: ticket.client_case_nr,
			description: ticket.description,
			location:
				ticket.location && siteLabel ? (
					<Typography.Link onClick={onViewSite}>{siteLabel}</Typography.Link>
				) : (
					siteLabel
				),
			// service: serviceObjects?.[ticket.service].label,
			turbine:
				ticket.turbine && turbineLabel ? (
					<Typography.Link onClick={onViewTurbine}>
						{turbineLabel}
					</Typography.Link>
				) : (
					turbineLabel
				),
			progress: (
				<CompletionProgress
					completedCount={ticket.items.filter((task) => task.completed === true).length}
					totalCount={ticket.items.length}
					intl={intl}
				/>
			),
			// created_at: ticket.created_at,
			status: status,
			report: <ReportDoneBadge reportDone={ticket.report_done} />,
			start: (
				<EditableDatePicker
					variableName="start"
					variableValue={ticket.start}
					intl={intl}
					onChange={
						ticket.id
							? (_field, value) =>
									dispatch(
										ticketActions.ticketDateEdit({
											id: ticket.id!,
											field: "start",
											value,
										}),
									)
							: undefined
					}
				/>
			),
			deadline: (
				<EditableDatePicker
					variableName="deadline"
					variableValue={ticket.deadline}
					intl={intl}
					onChange={
						ticket.id
							? (_field, value) =>
									dispatch(
										ticketActions.ticketDateEdit({
											id: ticket.id!,
											field: "deadline",
											value,
										}),
									)
							: undefined
					}
				/>
			),
			// updated_at: ticket.updated_at,
			assignee: (
				<EditableAssignees
					intl={intl}
					assignees={ticket.assignees
						.map((ta) =>
							ta.assignee != null
								? ticketSideload.assignees[ta.assignee]
								: null,
						)
						.filter((u): u is UserLimitedList => u != null)}
					technicians={technicians}
					onChange={
						ticket.id
							? (assigneeIds) =>
									dispatch(
										ticketActions.ticketAssigneesEdit({
											id: ticket.id!,
											assigneeIds,
										}),
									)
							: undefined
					}
				/>
			),
			active_checkin: (
				<ActiveCheckInBadge activeTimeTracking={ticket.active_time_tracking} />
			),
			total_time: (
				<Typography.Text type={ticket.total_time != null ? undefined : "secondary"}>
					{formatTotalTime(ticket.total_time)}
				</Typography.Text>
			),
			created_by: ticket.created_by
				? getUserDisplayName(ticketSideload.created_by[ticket.created_by])
				: null,
			actions: commonColumns.actionsValue(
				undefined,
				ticket,
				undefined,
				user,
				onView,
				onEdit,
				onDelete,
				undefined,
				"Work Order",
			),
		};
	});
};

type TicketFilterTitleProps = {
	dispatch: Dispatch;
	currentRoute: RouterState;
	statusItems: Status[];
	serviceItems: Services[];
	userItems: UserLimitedList[];
	turbineItems: Turbine[];
	clientItems: Client[];
	projectItems: Project[];
	form: FormInstance;
	intl: IntlShape<string>;
};

const FacilityFilterTitle = (props: TicketFilterTitleProps) => {
	const {
		form,
		dispatch,
		currentRoute,
		statusItems,
		serviceItems,
		userItems,
		turbineItems,
		clientItems,
		projectItems,
		intl,
	} = props;

	return (
		<ListActionBar
			intl={intl}
			form={form}
			onFilter={onFilter(dispatch, form, currentRoute)}
			onCancel={onCancel(dispatch, form, currentRoute)}
			filterTemplate={ticketFilter}
			currentFilter={currentRoute.params}
			searchBarPlaceholderKey={intl.formatMessage({
				id: "table_header_search_placeholder",
			})}
		>
			<DrawerItemWrapper>
				<StatusSelectorFormItem
					intl={intl}
					statusItems={statusItems}
					selectProps={{ allowClear: true }}
				/>
			</DrawerItemWrapper>
			{/* <DrawerItemWrapper>
        <ServiceSelectorFormItem
          intl={intl}
          serviceItems={serviceItems}
          selectProps={{ allowClear: true }}
        />
      </DrawerItemWrapper> */}
			<DrawerItemWrapper>
				<AssigneeSelectorFormItem
					intl={intl}
					userItems={userItems}
					selectProps={{ allowClear: true }}
				/>
			</DrawerItemWrapper>
			<DrawerItemWrapper>
				<CreatedBySelectorFormItem
					intl={intl}
					userItems={userItems}
					selectProps={{ allowClear: true }}
				/>
			</DrawerItemWrapper>
			<DrawerItemWrapper>
				<TurbineSelectorFormItem
					intl={intl}
					turbineItems={turbineItems}
					selectProps={{ allowClear: true }}
				/>
			</DrawerItemWrapper>
			<DrawerItemWrapper>
				<ClientSelectorFormItem
					intl={intl}
					clientItems={clientItems}
					selectProps={{ allowClear: true }}
				/>
			</DrawerItemWrapper>
			<DrawerItemWrapper>
				<Form.Item
					name="project"
					label={intl.formatMessage({ id: "project_label" })}
				>
					<Select
						allowClear
						showSearch
						options={projectItems.map((p) => ({ value: p.id, label: p.name }))}
						filterOption={(input, option) =>
							(option?.label?.toString() ?? "").toLowerCase().includes(input.toLowerCase())
						}
					/>
				</Form.Item>
			</DrawerItemWrapper>
		</ListActionBar>
	);
};

// Set by the dashboard's cards; the filter drawer has no field for it.
const attentionFilterMessageIds: Record<string, string> = {
	overdue: "dashboard_attention_overdue",
	due_soon: "dashboard_attention_due_soon",
	unstaffed: "dashboard_attention_unstaffed",
};

type TicketsPageProps = ReturnType<typeof withTickets> & DispatchProp;
export const TicketsPage = (props: TicketsPageProps) => {
	const {
		results,
		dispatch,
		currentRoute,
		statusItems,
		serviceItems,
		userItems,
		turbineItems,
		clientItems,
		projectItems,
		intl,
		user,
		ticketSideload,
		serviceObjects,
		technicians,
		modalAPI,
	} = props;

	const pagination = results.pagination;

	const [form] = Form.useForm();

	if (
		!(
			currentRoute &&
			statusItems &&
			serviceItems &&
			ticketSideload &&
			turbineItems &&
			clientItems
		)
	) {
		return (
			// <Flex align="center" gap="medium">
			<Spin indicator={<LoadingOutlined spin />} size="large" />
			// </Flex>
		);
	}

	const rowData: rowDataType = {
		user,
		results,
		ticketSideload,
		serviceObjects,
		technicians,
	};

	return (
		<ConnectedAppLayout>
			<PageTitleContainer
				title={intl.formatMessage({ id: "table_header_name" })}
				subtitle={intl.formatMessage(
					{ id: "table_header_number_of_items" },
					{ no: results.pagination.total_items },
				)}
				extra={
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={() =>
							dispatch(routerActions.navigateTo(routeNames.TICKET_CREATE))
						}
					>
						{intl.formatMessage({ id: "ticket_create_title" })}
					</Button>
				}
			>
				<Card
					title={
						<FacilityFilterTitle
							intl={intl}
							statusItems={statusItems}
							serviceItems={serviceItems}
							userItems={userItems}
							turbineItems={turbineItems}
							clientItems={clientItems}
							projectItems={projectItems}
							form={form}
							dispatch={dispatch}
							currentRoute={currentRoute}
						/>
					}
				>
					<Flex vertical gap="small">
						{currentRoute.params.attention in attentionFilterMessageIds && (
							<Flex>
								<Tag
									closable
									color="processing"
									onClose={() =>
										dispatch(
											routerActions.navigateTo(
												routeNames.TICKET_LIST,
												sanitizedFilter({
													...currentRoute.params,
													attention: undefined,
													page: 1,
												}),
											),
										)
									}
								>
									{intl.formatMessage(
										{ id: "ticket_list_attention_filter" },
										{
											filter: intl.formatMessage({
												id: attentionFilterMessageIds[
													currentRoute.params.attention
												],
											}),
										},
									)}
								</Tag>
							</Flex>
						)}
						<TableWrapper
							data={rowData}
							getRows={getTableRows}
							getColumns={getColumns}
							intl={intl}
							modalApi={modalAPI}
							route={currentRoute}
							applyFilterAction={(filterParams) =>
								routerActions.navigateTo(routeNames.TICKET_LIST, filterParams)
							}
							pagination={pagination as PaginationAndFilter}
						/>
					</Flex>
				</Card>
			</PageTitleContainer>
		</ConnectedAppLayout>
	);
};

const withTickets = (state: RootState) => ({
	results: state.ticket.list,
	ticketSideload: state.ticket.sideload,
	currentRoute: state.router.route,
	statusItems: state.ticket.statuses,
	serviceItems: state.ticket.services,
	serviceObjects: state.ticket.servicesObj,
	userItems: state.userstate.list.results,
	turbineItems: state.ticketData.turbines,
	clientItems: state.ticketData.clients,
	projectItems: state.ticketData.projects ?? [],
	technicians: state.ticketData.technicians ?? [],
	intl: state.intl.datetimeIntl,
	modalAPI: state.appFeedback.modal,
	user: state.userstate.user,
});
export const ConnectedTicketsPage = connect(withTickets)(TicketsPage);

export default ConnectedTicketsPage;
